import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  PencilIcon,
  PlusIcon,
  PhoneIcon,
  EnvelopeIcon,
  DocumentTextIcon,
  BriefcaseIcon,
  BanknotesIcon,
  ChatBubbleLeftRightIcon,
  FolderIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import { Button } from '../ui/Button';
import BackLink from '../ui/BackLink';
import { Card } from '../ui/Card';
import StatusPill, { SignaturePill, humanizeEnum } from '../ui/StatusPill';
import { SalespeopleChips } from '../ui/SalespeopleChips';
import { resolveQuoteSalespeople, quoteDateValue } from '../../utils/normaliseSalespeople';
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../ui/Table';
import { FilterChip } from '../ui/ListShell';
import { GoldenCustomerCard } from '../ui';
import { Alert } from '../ui/Alert';
import { SkeletonCard } from '../ui/Skeleton';
import { Dialog, DialogHeader, DialogBody } from '../ui/Dialog';
import { Input } from '../ui/Input';
import { NotesPanel } from '../ui/NotesPanel';
import AddCustomerDrawer from './AddCustomerDrawer';
import api, { customerAPI, unwrapList } from '../../services/api';
import { formatPhoneInput } from '../../utils/phone';
import { formatCurrency } from '../../utils/formatCurrency';
import { resolveDisplayStatus } from '../../quote-builder';
import { getQuoteRoute } from '../../utils/quoteRouting';
import { originState } from '../../utils/backOrigin';
import { isConverted } from '../../utils/isConverted';
import { deriveJobStatus } from '../../utils/jobStatus';
import { formatQuoteNumber, formatJobNumber, formatInvoiceNumberFor } from '../../utils/displayId';
import useTelemetry from '../../hooks/useTelemetry';
import Tabs from '../ui/Tabs';
import EmptyState from '../ui/EmptyState';
import { AttachModal } from '../attachments/AttachModal';
import { formatDate } from '../../utils/formatDate';
import { useAuth, useOptionalAuth } from '../../contexts/AuthContext';
import { isFieldTech } from '../../utils/isFieldTech';
import BulkActionBar, { BulkRowCheckbox } from '../ui/BulkActionBar';
import { useToast } from '../ui/Toast';
import InvoiceRowMenu from '../invoices/InvoiceRowMenu';
import useTableSort from '../ui/useTableSort';
import MarkPaidConfirm from '../invoices/MarkPaidConfirm';
import { canMarkInvoicePaid } from '../invoices/invoiceLifecycle';

// IDI-392: the customer's Quotes tab should show a quote that WON and
// converted to a job — that's a real, completed quote in the customer's
// history. Hiding it made the tab badge undercount (customer af697f72 had 2
// quotes, tab showed 1). Scoped to THIS surface only — QuotesPage's main
// listing keeps the IDI-130 ruling ("converted quote disappears from the
// quotes landing page") unchanged.
function isHiddenFromCustomerQuotesTab(q) {
  if (!q) return false;
  const status = (q.status || '').toLowerCase();
  if (status === 'won') return false;
  return isConverted(q);
}

// PT-53b F2 (2026-08-03): tab strip migrated to shared <Tabs> atom — same shape
// as JobDetail (JobDetail.js:838) + ReportsPage. `key` renamed to `value` to
// match the Tabs atom's prop contract. Previously CustomerDetail hand-rolled
// its own tab strip with a divergent badge style (`bg #f6f6f6`, near-white on
// white card) — which is why operator observation reported badges as absent.
// Migration adopts the canonical badge (`bg var(--color-surface-sunken)`,
// visible on white) and closes DEC-58's count-badge parity + SD-6's
// consumer-measurement requirement.
const TABS = [
  { value: 'quotes', label: 'Quotes', icon: DocumentTextIcon },
  { value: 'jobs', label: 'Jobs', icon: BriefcaseIcon },
  { value: 'invoices', label: 'Invoices', icon: BanknotesIcon },
  { value: 'contacts', label: 'Contacts', icon: UsersIcon },
  { value: 'notes', label: 'Notes', icon: ChatBubbleLeftRightIcon },
  { value: 'documents', label: 'Files', icon: FolderIcon },
];

// IDI-582 S4: a field tech's customer page — Jobs, Contacts, Notes, Files,
// opening on Jobs. No quotes or invoices (the APIs refuse them).
const FIELD_TECH_TABS = ['jobs', 'contacts', 'notes', 'documents'];

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { emit } = useTelemetry();
  const fieldTech = isFieldTech(useOptionalAuth()?.user);
  const tabs = fieldTech ? TABS.filter(t => FIELD_TECH_TABS.includes(t.value)) : TABS;
  const [customer, setCustomer] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [jobs, setJobs] = useState([]);
  // IDI-216: track whether the paginated jobs walk terminated on its own
  // (complete=true, empty state authoritative) or bailed at MAX_PAGES
  // (complete=false, empty state is a lie).
  const [jobsIncomplete, setJobsIncomplete] = useState(false);
  // IDI-216 progressive-render (Portica 2026-08-30 §7 with guard): jobs
  // pagination costs ~18s on tenant 270 (5 sequential /v1/jobs pages).
  // Prior shape gated the whole page on the full Promise.allSettled; the
  // page rendered as skeleton for 18s and read as broken. Now the page
  // header + tabs + non-jobs data render as soon as the customer record
  // arrives; jobsLoading stays true during the walk so the Jobs tab shows
  // its own spinner. Portica's guard: keep the slowness visible where it
  // originates so IDI-216 Shape A (backend GSI) does not lose its driver.
  const [jobsLoading, setJobsLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // IDI-157: Back from a quote/job opened on one of this customer's tabs
  // returns here with that tab active (location.state.tab); else Quotes.
  const [activeTab, setActiveTab] = useState(() => {
    const tab = location.state && location.state.tab;
    return tabs.some(t => t.value === tab) ? tab : (fieldTech ? 'jobs' : 'quotes');
  });
  const [inactivateModalOpen, setInactivateModalOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setJobsLoading(true);
      // IDI-216 progressive-render: get the customer record first and
      // release the page gate immediately. Portica measured 18s to
      // first content pre-fix (jobs walk gated the whole page); post-fix
      // the header + tabs render as soon as this resolves.
      const cust = await api.getCustomer(id);
      setCustomer(cust);
      setLoading(false);
      // IDI-141: fan-out safety. Any one of these rejecting used to reject
      // the whole Promise.all and route to the outer catch, wiping the
      // customer detail view. Switch to allSettled so a partial failure
      // renders a partial view; log rejections loudly (no silent empty).
      //
      // The jobs arm runs in parallel with quotes + invoices, and its own
      // loading state is tracked via jobsLoading (set false in the jRes
      // handler below). Pre-fix this whole allSettled gated the page's
      // loading state; now only the Jobs tab's spinner tracks the walk.
      // IDI-582 S4: a field tech never fetches quotes or invoices (both APIs
      // 403 them); their tabs are hidden, so an empty list stands in.
      const fetchQuotes = fieldTech
        ? () => Promise.resolve([])
        : () => api.getQuotes({ customerId: id }).then(r => {
          const list = Array.isArray(r) ? r : unwrapList(r, 'quotes', 'CustomerDetail').list;
          return list.filter(x => String(x.customerId || x.customer_id || '') === String(id));
        });
      const fetchInvoices = fieldTech
        ? () => Promise.resolve([])
        : () => api.getInvoices({ customerId: id, customer_id: id })
          .then(r => (Array.isArray(r) ? r : []).filter(x => x.customerId === id || x.customer_id === id))
          .catch(() => []);
      const [qRes, jRes, iRes] = await Promise.allSettled([
        // IDI-216: /v1/quotes now filters server-side on customerId (see
        // backend-go/cmd/quotes/main.go::listQuotes). Client-side filter kept
        // as a defensive backstop (harmless no-op once the server filter is
        // correct; protects against a future regression silently going
        // unnoticed the way the original bug did).
        fetchQuotes(),
        // IDI-216: /v1/jobs now filters server-side on customerId. The
        // client-side pagination-walk stopgap (fetchAllJobsForCustomer.js)
        // is retired — a single bounded call replaces up to 20 sequential
        // page fetches (Portica measured ~18s on tenant 270 pre-fix).
        // unwrapList handles both the bare-array and enveloped response
        // shapes (envelope only appears when `limit` is passed).
        //
        // AI-review fix (2026-08-31): a customer with MORE than 250 jobs
        // (limit sent below) would previously get exactly 250 back with
        // hasMore=true silently discarded — the call "succeeds" but the
        // result is truncated, and jComplete must reflect that, not just
        // whether the promise resolved. Surface { list, hasMore } instead
        // of collapsing to a bare array so the caller can tell the
        // difference between "this customer has 3 jobs" and "this customer
        // has at least 250 jobs, we stopped counting."
        api.getJobs({ customerId: id, limit: 250 }).then(r => {
          const { list, hasMore } = unwrapList(r, 'jobs', 'CustomerDetail');
          const filtered = list.filter(j => String(j.customerId || j.customer_id || '') === String(id));
          return { list: filtered, hasMore };
        }),
        // DEF-147 (2026-08-04): IDI-216 now honors `customerId` server-side.
        // `customer_id` is still sent defensively (server ignores it; canonical
        // spelling only, per DEC-61) but no longer needed for correctness.
        // Client-side filter kept as a defensive backstop, same rationale as
        // the quotes arm above.
        fetchInvoices(),
      ]);
      if (qRes.status === 'rejected') console.error('[CustomerDetail] getQuotes failed', qRes.reason);
      if (jRes.status === 'rejected') console.error('[CustomerDetail] getJobs failed', jRes.reason);
      if (iRes.status === 'rejected') console.error('[CustomerDetail] getInvoices failed', iRes.reason);
      const q   = qRes.status === 'fulfilled' ? qRes.value : [];
      // IDI-216 (AI-review fix): jobs arm now returns { list, hasMore } from
      // a single filtered call. "Complete" means the call succeeded AND the
      // server said there's nothing more — a truncated-but-successful
      // response (hasMore=true) must NOT be reported as complete, or a
      // customer with 250+ jobs would silently see only the first page with
      // no indication anything was cut off.
      const jVal = jRes.status === 'fulfilled' ? jRes.value : { list: [], hasMore: true };
      const j = jVal.list || [];
      const jComplete = jRes.status === 'fulfilled' && !jVal.hasMore;
      const inv = iRes.status === 'fulfilled' ? iRes.value : [];
      setQuotes(q);
      setJobs(j);
      setJobsIncomplete(!jComplete);
      setJobsLoading(false);
      setInvoices(Array.isArray(inv) ? inv : []);
    } catch {
      setError('Failed to load customer.');
      // On outer catch: release both loading gates so the page renders
      // its error state rather than staying stuck in skeleton.
      setLoading(false);
      setJobsLoading(false);
    }
  }, [id, fieldTech]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // IDI-582 S4: if the user resolves after mount and is a field tech, move
  // off any tab they can't see (useState's initialiser only runs once).
  useEffect(() => {
    if (fieldTech && !FIELD_TECH_TABS.includes(activeTab)) setActiveTab('jobs');
  }, [fieldTech, activeTab]);

  // IDI-518: refresh just the invoices in place after a row/bulk action.
  const reloadInvoices = useCallback(async () => {
    try {
      const r = await api.getInvoices({ customerId: id, customer_id: id });
      const list = (Array.isArray(r) ? r : []).filter(x => x.customerId === id || x.customer_id === id);
      setInvoices(list);
    } catch (e) {
      console.error('[CustomerDetail] reload invoices failed', e);
    }
  }, [id]);

  if (loading) return <div className="space-y-4 p-6"><SkeletonCard /><SkeletonCard /></div>;
  if (!customer) return (
    // PR-43 (§3): mirrors JobDetail's not-found shape — alert + BackLink.
    <div className="px-6">
      <Alert variant="error">Customer not found.</Alert>
      <div className="mt-4">
        <BackLink to="/customers" label="Back to Customers" />
      </div>
    </div>
  );

  // IDI-157: the Back label a quote/job opened from this customer shows.
  // Same display-name rule as GoldenCustomerCard.
  const customerDisplayName = customer.use_company_as_display && customer.company
    ? customer.company
    : (customer.name || 'Customer');
  const backLabel = `Back to ${customerDisplayName}`;

  return (
    <div className="px-6">
      {error && <Alert variant="error" className="mb-4" onClose={() => setError(null)}>{error}</Alert>}

      {/* Back + Action Bar */}
      <div className="flex items-center justify-between mb-4">
        <BackLink to="/customers" label="Back to Customers" />
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => setEditOpen(true)}>
            <PencilIcon className="h-4 w-4 mr-1" />
            Edit
          </Button>
          {/* PT-49 CTA rule: only two actions (Edit + Inactivate) → named button, not ellipsis menu */}
          <Button
            variant="danger"
            onClick={() => setInactivateModalOpen(true)}
          >
            Inactivate
          </Button>
        </div>
      </div>

      {/* Customer Header — Golden Card (Iter18 Customer Detail G) */}
      <div className="mb-6">
        <GoldenCustomerCard
          customer={customer}
          surface="customer"
          hideMargin={fieldTech}
          onEdit={() => setEditOpen(true)}
        />
      </div>

      {/* Tabs — PT-53b F2 (2026-08-03): migrated from hand-rolled <nav>/<button>
          to the shared <Tabs> atom. Per-tab counts derived per DEC-58 (Job has
          them, Customer now matches — and with the same rendering). */}
      <Tabs
        tabs={tabs.map((tab) => {
          let count;
          switch (tab.value) {
            case 'quotes':    count = quotes.filter(q => !isHiddenFromCustomerQuotesTab(q)).length; break;
            case 'jobs':      count = jobs.length; break;
            case 'invoices':  count = invoices.length; break;
            case 'contacts':  count = (customer?.contacts || []).length; break;
            case 'notes':     count = (customer?.notes_list || customer?.activity_notes || []).length; break;
            case 'documents': count = (customer?.documents || []).length; break;
            default:          count = undefined;
          }
          return { ...tab, count };
        })}
        value={activeTab}
        onChange={setActiveTab}
        className="mb-4"
      />

      {/* Tab Content */}
      <div role="tabpanel">
        {activeTab === 'quotes' && <QuotesTab quotes={quotes} customerId={id} navigate={navigate} origin={originState({ from: `/customers/${id}`, fromTab: 'quotes', label: backLabel })} />}
        {activeTab === 'jobs' && <JobsTab jobs={jobs} jobsIncomplete={jobsIncomplete} jobsLoading={jobsLoading} navigate={navigate} origin={originState({ from: `/customers/${id}`, fromTab: 'jobs', label: backLabel })} />}
        {activeTab === 'invoices' && <InvoicesTab invoices={invoices} navigate={navigate} reload={reloadInvoices} />}
        {activeTab === 'contacts' && <ContactsTab customer={customer} customerId={id} onRefresh={fetchData} emit={emit} />}
        {activeTab === 'notes' && <NotesTab customer={customer} customerId={id} onRefresh={fetchData} emit={emit} />}
        {activeTab === 'documents' && <DocumentsTab customer={customer} customerId={id} onRefresh={fetchData} emit={emit} />}
      </div>

      {/* Inactivate Modal */}
      <InactivateModal
        open={inactivateModalOpen}
        onClose={() => setInactivateModalOpen(false)}
        customer={customer}
        onConfirm={async (reason) => {
          await customerAPI.updateCustomer(id, { status: 'inactive', inactivate_reason: reason });
          emit('customer.inactivated', { customer_id: id, reason });
          setInactivateModalOpen(false);
          fetchData();
        }}
      />

      {/* Iter18 Customers polish: Edit uses AddCustomerDrawer (CU-1 parity)
          — same drawer as the landing page, so the edit form matches the
          add form. The legacy inline EditCustomerForm below is unused after
          this change. */}
      <AddCustomerDrawer
        open={editOpen}
        onClose={() => setEditOpen(false)}
        editCustomer={customer}
        onSaved={() => {
          setEditOpen(false);
          fetchData();
        }}
      />
    </div>
  );
}
// IDI-479: click-to-sort columns for the three customer tabs. Accessors read
// the same value each cell renders.
const CUSTOMER_QUOTE_SORT_COLUMNS = [
  { key: 'project', type: 'text', value: (q) => q.title || q.projectName || q.project_name || formatQuoteNumber(q) },
  { key: 'salesperson', type: 'text', value: (q) => resolveQuoteSalespeople(q)[0]?.name || null },
  { key: 'amount', type: 'money', value: (q) => q.total_amount ?? q.totalAmount ?? null },
  { key: 'acknowledgments', type: 'text', value: (q) => humanizeEnum(q.signatureStatus || q.signature_status || '') || null },
  { key: 'status', type: 'text', value: (q) => humanizeEnum(resolveDisplayStatus(q.status, 1)) || null },
  { key: 'quoteDate', type: 'date', value: (q) => quoteDateValue(q) },
];
const CUSTOMER_JOB_SORT_COLUMNS = [
  { key: 'project', type: 'text', value: (j) => j.title || formatJobNumber(j) },
  { key: 'salesperson', type: 'text', value: (j) => j.salesperson || j.assignedTo || j.assigned_to || null },
  // IDI-779: the Job page's status (deriveJobStatus), not the stored one.
  { key: 'status', type: 'text', value: (j) => humanizeEnum(deriveJobStatus(j)) || null },
  { key: 'created', type: 'date', value: (j) => j.createdAt || j.created_at || null },
];
const CUSTOMER_INVOICE_SORT_COLUMNS = [
  { key: 'number', type: 'text', value: (inv) => formatInvoiceNumberFor(inv) || null },
  { key: 'amount', type: 'money', value: (inv) => inv.totalAmount ?? inv.total_amount ?? null },
  { key: 'status', type: 'text', value: (inv) => humanizeEnum(inv.status) || null },
  { key: 'created', type: 'date', value: (inv) => inv.createdAt || inv.created_at || null },
];

function QuotesTab({ quotes, customerId, navigate, origin }) {
  const visibleQuotes = quotes.filter(q => !isHiddenFromCustomerQuotesTab(q));
  const { sortedRows: sortedQuotes, getSortProps } = useTableSort(visibleQuotes, CUSTOMER_QUOTE_SORT_COLUMNS);
  if (visibleQuotes.length === 0) {
    return (
      <div className="text-center py-10">
        <DocumentTextIcon className="h-10 w-10 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500 mb-3">No quotes for this customer yet.</p>
        <Button onClick={() => navigate(`/quotes/add?customerId=${customerId}`)}>
          <PlusIcon className="h-4 w-4 mr-1" />
          Create Quote
        </Button>
      </div>
    );
  }
  return (
    <Card className="overflow-hidden">
      <Table bordered={false}>
        <TableHead>
          <TableRow>
            <TableHeader {...getSortProps('project')}>Project</TableHeader>
            <TableHeader {...getSortProps('salesperson')}>Salesperson</TableHeader>
            <TableHeader align="right" {...getSortProps('amount')}>Amount</TableHeader>
            {/* IDI-164 / D-10: "Acknowledgments"; pill values unchanged. */}
            <TableHeader {...getSortProps('acknowledgments')}>Acknowledgments</TableHeader>
            <TableHeader {...getSortProps('status')}>Status</TableHeader>
            {/* IDI-164: Quote Date = date, else createdAt. No Job Type here. */}
            <TableHeader {...getSortProps('quoteDate')}>Quote Date</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedQuotes.map(q => (
            <TableRow key={q.id} onClick={() => navigate(getQuoteRoute(q), { state: origin })} className="cursor-pointer">
              <TableCell className="font-semibold" href={getQuoteRoute(q)} hrefState={origin}>
                {q.title || q.projectName || q.project_name || formatQuoteNumber(q)}
                <span className="block text-xs font-normal" style={{ color: "var(--color-text-muted)" }}>{formatQuoteNumber(q)}</span>
              </TableCell>
              <TableCell>
                {(() => {
                  // IDI-164: same reader as the Quotes landing — named
                  // salespersons[] → salespersonName → '—'; never a raw id.
                  const norm = resolveQuoteSalespeople(q);
                  if (norm.length === 0) return "—";
                  return <SalespeopleChips salespeople={norm} interactive={false} />;
                })()}
              </TableCell>
              <TableCell align="right">
                {/* IDI-215: total_amount (live, editable) before totalAmount (stale
                    legacy alias) — see QuotesPage.js/QuoteDetail.js for the same fix. */}
                {(q.total_amount ?? q.totalAmount) != null
                  ? formatCurrency(q.total_amount ?? q.totalAmount)
                  : "—"}
              </TableCell>
              <TableCell>
                {(q.signatureStatus || q.signature_status)
                  ? <SignaturePill signatureStatus={q.signatureStatus || q.signature_status} size="sm" />
                  : "—"}
              </TableCell>
              <TableCell><StatusPill status={resolveDisplayStatus(q.status, 1)} /></TableCell>
              {/* IDI-238: reader was `q.created_at` (snake_case only). Production
                  wire on /v1/quotes?customerId= and /v1/jobs?customerId= delivers
                  camelCase — verified live 2026-09-05 against /customers/8006 on
                  tenant 270: Quotes tab 31/31 em-dash while wire had createdAt
                  200/200; Jobs tab 15/15 em-dash while wire had createdAt 15/15.
                  formatDate(undefined) returns '' and the `|| "—"` at the call
                  site renders the dash — every existing snake_case fixture in
                  this repo passed because no test exercised the camelCase path.
                  This PR fixes both consumers in this file (Quotes here, Jobs
                  below at :450 — same file, one PR per §7). The sibling Invoices
                  reader at :515 already reads camelCase-first — it's the exemplar.
                  QuotesPage.js:776 uses the same pattern.

                  Not touched here: QuotesPage.js:571 defines an inline unpadded
                  formatDate that ALSO has the local-accessor TZ shift class of
                  IDI-259. utils/formatDate.js:9 rules "any second implementation
                  of date formatting is a defect, not a shortcut" — so the fix
                  is to delete the inline copy and route through the shared
                  utility. Recorded on IDI-101 per operator; not filed
                  separately. Fixing IDI-259 in the shared utility will NOT
                  reach the inline copy — deletion is the fix. */}
              <TableCell>{formatDate(quoteDateValue(q)) || "—"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

// ─── Jobs Tab ───
// IDI-216: `jobsIncomplete` is set when the getJobs({ customerId }) call
// rejects — in that case we cannot honestly claim "no jobs yet," because we
// did not finish looking. The copy differs to match the epistemic state.
// Retained post-fix even though the common case is now a single successful
// call: a rejection is still possible (network error, Lambda cold-start
// timeout) and deserves the same honest treatment it got under the old
// paginated-walk stopgap.
//
// `jobsLoading` is true while the single filtered call is in flight.
function JobsTab({ jobs, jobsIncomplete = false, jobsLoading = false, navigate, origin }) {
  const { sortedRows: sortedJobs, getSortProps } = useTableSort(jobs, CUSTOMER_JOB_SORT_COLUMNS);
  if (jobsLoading) {
    return (
      <div className="flex items-center justify-center py-12" role="status" aria-live="polite">
        <div className="text-sm text-gray-500">
          Loading jobs…
        </div>
      </div>
    );
  }
  if (jobs.length === 0) {
    return (
      <div className="text-center py-10">
        <BriefcaseIcon className="h-10 w-10 text-gray-300 mx-auto mb-3" />
        {jobsIncomplete ? (
          <p className="text-gray-500">
            Jobs list could not be fully loaded. Refresh to try again.
          </p>
        ) : (
          <p className="text-gray-500">No jobs yet. Jobs are created from converted quotes.</p>
        )}
      </div>
    );
  }
  return (
    <Card className="overflow-hidden">
      {jobsIncomplete && (
        // AI-review fix (2026-08-31): jobsIncomplete can be true with a
        // non-empty jobs list (a truncated-but-successful response) --
        // this banner is the only signal for that case.
        <div
          className="px-4 py-2 text-sm border-b"
          style={{ borderColor: 'var(--color-warning)', color: 'var(--color-warning)' }}
          role="status"
        >
          Showing a partial list — this customer may have more jobs than shown.
        </div>
      )}
      <Table bordered={false}>
        <TableHead>
          <TableRow>
            <TableHeader {...getSortProps('project')}>Project</TableHeader>
            <TableHeader {...getSortProps('salesperson')}>Salesperson</TableHeader>
            <TableHeader {...getSortProps('status')}>Status</TableHeader>
            <TableHeader {...getSortProps('created')}>Date (Created)</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedJobs.map(j => (
            <TableRow key={j.id} onClick={() => navigate(`/jobs/${j.id}`, { state: origin })} className="cursor-pointer">
              <TableCell className="font-semibold" href={`/jobs/${j.id}`} hrefState={origin}>
                {j.title || formatJobNumber(j)}
                <span className="block text-xs font-normal" style={{ color: "var(--color-text-muted)" }}>{formatJobNumber(j)}</span>
              </TableCell>
              <TableCell>{j.salesperson || j.assignedTo || j.assigned_to || "—"}</TableCell>
              <TableCell><StatusPill status={deriveJobStatus(j)} /></TableCell>
              {/* IDI-238: same defect + fix as the Quotes-tab reader at :350.
                  Wire delivers camelCase createdAt; snake_case-only reader
                  em-dashed 15/15 on /customers/8006 pre-fix (live-verified). */}
              <TableCell>{formatDate(j.createdAt || j.created_at) || "—"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

// ─── Invoices Tab ───
function InvoicesTab({ invoices, navigate, reload }) {
  const [filter, setFilter] = useState('all');
  const filtered = filter === 'all' ? invoices : invoices.filter(i => i.status === filter);
  // IDI-479: sort after the status filter; selection is id-based so it is
  // unaffected by row order.
  const { sortedRows: sortedInvoices, getSortProps } = useTableSort(filtered, CUSTOMER_INVOICE_SORT_COLUMNS);
  // IDI-518: bulk Mark paid — Sent rows only; select-all = Sent rows in the
  // current filter. One mark_paid per invoice, in turn; no new endpoint.
  const toast = useToast();
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const isSelectable = (inv) => canMarkInvoicePaid(inv);
  const selectableInView = filtered.filter(isSelectable);
  const selectableIds = new Set(invoices.filter(isSelectable).map(i => i.id));
  // Drop selections that are no longer Sent (e.g. after a reload).
  const selected = selectedIds.filter(sid => selectableIds.has(sid));
  // Prune the stored selection whenever the invoice list changes (reload),
  // so stale ids never accumulate in state.
  useEffect(() => {
    const live = new Set(invoices.filter(isSelectable).map(i => i.id));
    setSelectedIds(prev => {
      const next = prev.filter(sid => live.has(sid));
      return next.length === prev.length ? prev : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoices]);
  const allInViewSelected = selectableInView.length > 0 && selectableInView.every(i => selected.includes(i.id));
  const toggleOne = (sid, on) => setSelectedIds(prev => (on ? [...new Set([...prev, sid])] : prev.filter(x => x !== sid)));
  const toggleAll = (on) => setSelectedIds(prev => {
    const inView = selectableInView.map(i => i.id);
    return on ? [...new Set([...prev, ...inView])] : prev.filter(x => !inView.includes(x));
  });

  const runBulkMarkPaid = async (paidDate) => {
    setBulkBusy(true);
    const targets = invoices.filter(i => selected.includes(i.id));
    let ok = 0;
    const failed = [];
    for (const inv of targets) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await api.markInvoicePaid(inv.id, paidDate);
        ok += 1;
      } catch {
        failed.push(formatInvoiceNumberFor(inv));
      }
    }
    setBulkBusy(false);
    setBulkOpen(false);
    setSelectedIds([]);
    if (ok > 0) toast.success(`${ok} invoice${ok === 1 ? '' : 's'} marked paid`);
    if (failed.length > 0) toast.error(`Could not mark paid: ${failed.join(', ')}`);
    // The marks already happened; a failed refresh must not reject the click.
    try {
      await reload?.();
    } catch (e) {
      console.error('[CustomerDetail] invoice refresh after bulk Mark paid failed', e);
    }
  };

  if (invoices.length === 0) {
    return (
      <EmptyState
        mode="first-run"
        icon={BanknotesIcon}
        title="No invoices yet"
        description="Invoices are created from jobs."
      />
    );
  }
  return (
    <div>
      {/* Iter18 Customer Detail F: filter pills use canonical FilterChip
          (with count badges) matching Customers landing page. */}
      <div className="flex flex-wrap gap-2 mb-3">
        {['all', 'draft', 'sent', 'paid'].map(s => {
          const count = s === 'all' ? invoices.length : invoices.filter(i => i.status === s).length;
          return (
            <FilterChip
              key={s}
              label={s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
              active={filter === s}
              count={count}
              onClick={() => setFilter(s)}
            />
          );
        })}
      </div>
      <Card className="overflow-hidden">
        <Table bordered={false}>
          <TableHead>
            <TableRow>
              <TableHeader className="w-10">
                {selectableInView.length > 0 && (
                  <BulkRowCheckbox
                    checked={allInViewSelected}
                    onChange={toggleAll}
                    label="Select all sent invoices"
                  />
                )}
              </TableHeader>
              <TableHeader {...getSortProps('number')}>Invoice #</TableHeader>
              <TableHeader align="right" {...getSortProps('amount')}>Amount</TableHeader>
              <TableHeader {...getSortProps('status')}>Status</TableHeader>
              <TableHeader {...getSortProps('created')}>Date (Created)</TableHeader>
              <TableHeader align="right"><span className="sr-only">Actions</span></TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedInvoices.map(inv => (
              <TableRow key={inv.id} onClick={() => navigate(`/invoices/${inv.id}`)} className="cursor-pointer">
                <TableCell className="w-10" onClick={(e) => e.stopPropagation()}>
                  {isSelectable(inv) && (
                    <BulkRowCheckbox
                      checked={selected.includes(inv.id)}
                      onChange={(on) => toggleOne(inv.id, on)}
                      label={`Select ${formatInvoiceNumberFor(inv)}`}
                    />
                  )}
                </TableCell>
                <TableCell className="font-semibold" href={`/invoices/${inv.id}`}>
                  {formatInvoiceNumberFor(inv)}
                  {(inv.jobTitle || inv.job_title) && <span className="block text-xs font-normal" style={{ color: "var(--color-text-muted)" }}>{inv.jobTitle || inv.job_title}</span>}
                </TableCell>
                <TableCell align="right">
                  {(inv.totalAmount ?? inv.total_amount) != null
                    ? formatCurrency(inv.totalAmount ?? inv.total_amount)
                    : "—"}
                </TableCell>
                <TableCell><StatusPill status={inv.status} /></TableCell>
                <TableCell>{formatDate(inv.createdAt || inv.created_at) || "—"}</TableCell>
                <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                  <InvoiceRowMenu invoice={inv} onChanged={reload} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      <BulkActionBar
        count={selected.length}
        onClear={() => setSelectedIds([])}
        actions={[
          { label: 'Mark paid', onClick: () => setBulkOpen(true), disabled: bulkBusy },
        ]}
      />
      {bulkOpen && (
        <MarkPaidConfirm
          open={bulkOpen}
          count={selected.length}
          busy={bulkBusy}
          onConfirm={runBulkMarkPaid}
          onClose={() => setBulkOpen(false)}
        />
      )}
    </div>
  );
}

// ─── Contacts Tab ───
function ContactsTab({ customer, customerId, onRefresh, emit }) {
  const [addModalOpen, setAddModalOpen] = useState(false);
  const contacts = customer.contacts || [];

  const handleAddContact = async (contactData) => {
    await customerAPI.updateCustomer(customerId, {
      contacts: [...contacts, { ...contactData, id: `contact_${Date.now()}` }],
    });
    emit('customer.contact_added', { customer_id: customerId });
    setAddModalOpen(false);
    onRefresh();
  };

  return (
    <div>
      <div className="flex justify-end mb-3">
        <Button size="sm" onClick={() => setAddModalOpen(true)}>
          <PlusIcon className="h-4 w-4 mr-1" />
          Add Contact
        </Button>
      </div>

      {contacts.length === 0 ? (
        <div className="text-center py-10">
          <UsersIcon className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No contacts added yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {contacts.map((contact, idx) => (
            <Card key={contact.id || idx} className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-gray-900">{contact.name}</p>
                    {contact.is_primary && (
                      <span
                        className="text-xs px-1.5 py-0.5 rounded"
                        style={{ backgroundColor: 'var(--neutral-100)', color: 'var(--color-text-muted)' }}
                      >
                        Primary
                      </span>
                    )}
                  </div>
                  {contact.title && <p className="text-xs text-gray-500">{contact.title}</p>}
                  {contact.phone && (
                    <div className="flex items-center gap-1.5 mt-1.5">
                      <PhoneIcon className="h-3.5 w-3.5 text-gray-400" />
                      <a href={`tel:${contact.phone}`} className="text-xs text-primary-600 hover:underline">{contact.phone}</a>
                    </div>
                  )}
                  {contact.email && (
                    <div className="flex items-center gap-1.5 mt-1">
                      <EnvelopeIcon className="h-3.5 w-3.5 text-gray-400" />
                      <a href={`mailto:${contact.email}`} className="text-xs text-primary-600 hover:underline">{contact.email}</a>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Add Contact Modal */}
      <AddContactModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSave={handleAddContact}
      />
    </div>
  );
}

// ─── Notes Tab ───
function NotesTab({ customer, customerId, onRefresh, emit }) {
  const { user } = useAuth();
  const notes = (customer.notes_list || customer.activity_notes || []).map(n => ({
    id: n.id,
    content: n.text || n.content,
    author: n.author,
    author_id: n.author_id || n.authorId,
    created_at: n.created_at,
  }));

  const handleAddNote = async (content) => {
    // IDI-550: name the author (profile name) and record who (sub), never 'You'.
    const updatedNotes = [
      {
        id: `note_${Date.now()}`,
        text: content,
        created_at: new Date().toISOString(),
        author: user?.name || user?.email || 'Unknown',
        author_id: user?.id || null,
      },
      ...(customer.notes_list || customer.activity_notes || []),
    ];
    await customerAPI.updateCustomer(customerId, { notes_list: updatedNotes });
    emit('customer.note_added', { customer_id: customerId });
    onRefresh();
  };

  return (
    <NotesPanel notes={notes} onAdd={handleAddNote} />
  );
}

// ─── Files Tab ───
// Iter18 Customers polish: renamed from 'Documents' to 'Files' (matches Settings
// naming). Upload now uses FileReader.readAsDataURL so the file content is
// actually captured (was: only metadata was stored, so uploads appeared silent).
function DocumentsTab({ customer, customerId, onRefresh, emit }) {
  const [attachOpen, setAttachOpen] = useState(false);
  const documents = customer.documents || [];

  const handleAttached = () => {
    emit('customer.document_uploaded', { customer_id: customerId });
    onRefresh();
    setAttachOpen(false);
  };

  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div>
      <div className="flex justify-end mb-3">
        <Button variant="secondary" size="sm" onClick={() => setAttachOpen(true)}>
          <PlusIcon className="h-4 w-4 mr-1" />
          Upload
        </Button>
      </div>

      {documents.length === 0 ? (
        <EmptyState
          mode="first-run"
          icon={FolderIcon}
          title="No files uploaded yet"
          description="Upload documents related to this customer."
        />
      ) : (
        <Card className="overflow-hidden">
          <table className="data-table w-full">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Size</th>
                <th>Uploaded</th>
              </tr>
            </thead>
            <tbody>
              {documents.map((doc, idx) => (
                <tr key={doc.id || idx}>
                  <td>
                    <div className="flex items-center gap-2">
                      <FolderIcon className="h-4 w-4" style={{ color: 'var(--color-text-muted)' }} />
                      <span style={{ fontWeight: 500 }}>{doc.name}</span>
                    </div>
                  </td>
                  <td style={{ color: 'var(--color-text-muted)' }}>
                    {doc.type ? doc.type.split('/')[1] || doc.type : '\u2014'}
                  </td>
                  <td style={{ color: 'var(--color-text-muted)' }}>{formatSize(doc.size)}</td>
                  <td style={{ color: 'var(--color-text-muted)' }}>
                    {formatDate(doc.uploaded_at) || '\u2014'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <AttachModal
        open={attachOpen}
        onClose={() => setAttachOpen(false)}
        scope="customer"
        ownerId={customerId}
        ownerLabel={customer?.name || 'Customer'}
        onAttached={handleAttached}
      />
    </div>
  );
}


// ─── Add Contact Modal ───
function AddContactModal({ open, onClose, onSave }) {
  const [form, setForm] = useState({ name: '', phone: '', email: '', title: '', is_primary: false });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setForm({ name: '', phone: '', email: '', title: '', is_primary: false });
      setError(null);
    }
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError('Contact name is required');
      return;
    }
    try {
      setSaving(true);
      await onSave(form);
    } catch (err) {
      setError(err.message || 'Failed to add contact');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogHeader onClose={onClose}>Add Contact</DialogHeader>
      <DialogBody>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <Alert variant="error">{error}</Alert>}
          <div>
            <label htmlFor="contact-name" className="block text-sm font-medium text-gray-700 mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <Input
              id="contact-name"
              value={form.name}
              onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Contact name"
              required
              autoFocus
            />
          </div>
          <div>
            <label htmlFor="contact-title" className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <Input
              id="contact-title"
              value={form.title}
              onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
              placeholder="e.g., Project Manager"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="contact-phone" className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <Input
                id="contact-phone"
                type="tel"
                value={form.phone}
                onChange={(e) => setForm(prev => ({ ...prev, phone: formatPhoneInput(e.target.value) }))}
                placeholder="(555) 123-4567"
              />
            </div>
            <div>
              <label htmlFor="contact-email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <Input
                id="contact-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="email@company.com"
              />
            </div>
          </div>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.is_primary}
              onChange={(e) => setForm(prev => ({ ...prev, is_primary: e.target.checked }))}
              className="rounded border-[color:var(--color-border)]"
              style={{ accentColor: 'var(--color-action-primary)' }}
            />
            <span className="text-sm text-gray-700">Set as primary contact</span>
          </label>
          <div className="flex justify-end gap-3 pt-4 border-t border-[color:var(--color-border)]">
            <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving}>{saving ? 'Adding...' : 'Add Contact'}</Button>
          </div>
        </form>
      </DialogBody>
    </Dialog>
  );
}

// ─── Inactivate Modal ───
function InactivateModal({ open, onClose, customer, onConfirm }) {
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);

  const handleConfirm = async () => {
    try {
      setConfirming(true);
      await onConfirm(reason);
    } finally {
      setConfirming(false);
      setReason('');
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogHeader onClose={onClose}>Inactivate Customer</DialogHeader>
      <DialogBody>
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Are you sure you want to inactivate <strong>{customer?.name}</strong>? They will no longer appear in the active customers list.
          </p>
          <div>
            <label htmlFor="inactivate-reason" className="block text-sm font-medium text-gray-700 mb-1">
              Reason (optional)
            </label>
            <textarea
              id="inactivate-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="w-full rounded-md border border-[color:var(--color-border-subtle)] px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 resize-none"
              placeholder="Why is this customer being inactivated?"
            />
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-[color:var(--color-border)]">
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <Button variant="danger" onClick={handleConfirm} disabled={confirming}>
              {confirming ? 'Inactivating...' : 'Inactivate'}
            </Button>
          </div>
        </div>
      </DialogBody>
    </Dialog>
  );
}

// Named exports for testing (pattern: JobDetail.DEF-089.test.js)
export { JobsTab, InvoicesTab };
